import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import BrowsePage from "@/pages/BrowsePage";
import ProfilePage from "@/pages/ProfilePage";
import LikedPage from "@/pages/LikedPage";
import BuddhistSuitePage from "@/pages/BuddhistSuitePage";
import KatolikSuitePage from "@/pages/KatolikSuitePage";
import TermsAgreementPage from "@/pages/TermsAgreementPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import AdminBuddhistPage from "@/pages/AdminBuddhistPage";
import TutorialPage from "@/pages/TutorialPage";
import TicketPage from "@/pages/TicketPage";
import SeoLandingPage from "@/pages/SeoLandingPage";
import CategoryLandingPage from "@/pages/CategoryLandingPage";
import AIMatchPage from "@/pages/AIMatchPage";
import TermsPublicPage from "@/pages/TermsPublicPage";
import { ReportDialog } from "@/components/ReportDialog";

function MemberRoute({ children }) {
  const { token, role, member, loading, updateMember } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!token || role !== 'member') return <Navigate to="/auth" />;
  if (member && !member.terms_agreed) {
    return <TermsAgreementPage onAgreed={() => updateMember({ ...member, terms_agreed: true })} />;
  }
  return children;
}

function AdminRoute({ children }) {
  const { token, role, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!token || role !== 'admin') return <Navigate to="/admin" />;
  return children;
}

function LoadingScreen() {
  return (
    <div className="min-h-screen t-bg flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#E11D48] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/tutorial" element={<TutorialPage />} />
      <Route path="/ticket" element={<TicketPage />} />
      <Route path="/syarat-ketentuan" element={<TermsPublicPage />} />
      <Route path="/terms" element={<TermsPublicPage />} />
      <Route path="/jodoh/:slug" element={<SeoLandingPage />} />
      <Route path="/kategori/:slug" element={<CategoryLandingPage />} />
      <Route path="/browse" element={<MemberRoute><BrowsePage /></MemberRoute>} />
      <Route path="/liked" element={<MemberRoute><LikedPage /></MemberRoute>} />
      <Route path="/ai-match" element={<MemberRoute><AIMatchPage /></MemberRoute>} />
      <Route path="/buddhist" element={<MemberRoute><BuddhistSuitePage /></MemberRoute>} />
      <Route path="/katolik" element={<MemberRoute><KatolikSuitePage /></MemberRoute>} />
      <Route path="/profile" element={<MemberRoute><ProfilePage /></MemberRoute>} />
      <Route path="/admin" element={<AdminLoginPage />} />
      <Route path="/admin/dashboard" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
      <Route path="/admin/buddhist" element={<AdminRoute><AdminBuddhistPage /></AdminRoute>} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function useRemoveBadge() {
  useEffect(() => {
    const remove = () => {
      const el = document.getElementById('emergent-badge');
      if (el) el.remove();
    };
    remove();
    const observer = new MutationObserver(remove);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
}

function App() {
  useRemoveBadge();
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
            <ReportDialog />
            <Toaster richColors position="top-center" />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
